import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Filter, Plus, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { TopBar } from '@/components/shared/top-bar'
import { StatusBadge } from '@/components/shared/status-badge'
import { RoleChip } from '@/components/shared/role-chip'
import { EmptyState } from '@/components/shared/empty-state'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils/cn'
import { useProjectsQuery } from '../api/use-projects-query'
import { useAuthStore } from '@/features/auth/stores/use-auth-store'
import { CreateProjectWizard } from '../components/create-project-wizard'
import type { Project } from '../types/project'

const COLOR_FALLBACKS = [
  '#274690',
  '#286F6C',
  '#7B3FA0',
  '#C0392B',
  '#D4820A',
]

function getProjectColor(project: Project, index: number) {
  return project.color ?? COLOR_FALLBACKS[index % COLOR_FALLBACKS.length]
}

function ProjectCardSkeleton() {
  return (
    <div className="bg-card border border-border rounded-xl p-5 animate-pulse space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-10 h-6 bg-muted rounded" />
        <div className="h-4 bg-muted rounded flex-1" />
      </div>
      <div className="h-3 bg-muted rounded w-2/3" />
      <div className="flex gap-2">
        <div className="h-5 bg-muted rounded w-16" />
        <div className="h-5 bg-muted rounded w-20" />
      </div>
    </div>
  )
}

interface ProjectCardProps {
  project: Project
  index: number
  isFirst: boolean
  onClick: () => void
}

function ProjectCard({ project, index, isFirst, onClick }: ProjectCardProps) {
  const color = getProjectColor(project, index)

  return (
    <button
      onClick={onClick}
      className={cn(
        'bg-card border rounded-xl p-5 text-start w-full transition-all duration-150 hover:border-primary/60 hover:bg-muted/30 group',
        isFirst ? 'border-primary ring-1 ring-primary/40' : 'border-border'
      )}
    >
      {/* Header row */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-xs font-bold font-mono shrink-0"
          style={{ backgroundColor: color }}
        >
          {project.code?.slice(0, 3).toUpperCase() ?? '?'}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground truncate leading-tight">
            {project.name}
          </p>
          {project.department && (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {project.department}
            </p>
          )}
        </div>
      </div>

      {/* Badges row */}
      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <RoleChip role={project.role} />
        <StatusBadge status={project.status} />
      </div>

      {/* Footer row */}
      <div className="flex items-center justify-between pt-3 border-t border-border/60">
        <span className="text-xs text-muted-foreground font-mono">
          {project.recordCount.toLocaleString('fa-IR')} سابقه
        </span>
        {project.lastAccessed && (
          <span className="text-xs text-muted-foreground">
            آخرین بازدید: {new Date(project.lastAccessed).toLocaleDateString('fa-IR')}
          </span>
        )}
      </div>
    </button>
  )
}

export function ProjectSwitcherPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: projects = [], isLoading, isError, error, refetch } = useProjectsQuery()
  const { setActiveProjectId, user } = useAuthStore()
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const hasAdminMembership = projects.some((p) => p.role === 'admin')
  const canCreateProject = !!user?.isSuperUser || hasAdminMembership

  const filtered = projects.filter(
    (p) =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.code?.toLowerCase().includes(search.toLowerCase()) ||
      p.department?.toLowerCase().includes(search.toLowerCase())
  )

  const handleSelect = (project: Project) => {
    setActiveProjectId(project.id)
    navigate(`/projects/${project.id}/overview`)
  }

  const userBarProps = user
    ? {
        name: user.name ?? user.email,
        email: user.email,
        initials: (user.name ?? user.email)
          .split(' ')
          .map((w) => w[0])
          .join('')
          .slice(0, 2)
          .toUpperCase(),
      }
    : undefined

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <TopBar showProject={false} user={userBarProps} />

      <div className="flex-1 max-w-5xl mx-auto w-full px-6 py-10">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground mb-1">
            {t('projects.title', 'انتخاب پروژه')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('projects.subtitle', 'پروژه‌ای که می‌خواهید با آن کار کنید را انتخاب کنید')}
          </p>
        </div>

        {/* Search + filter */}
        <div className="flex gap-2 mb-6">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('projects.searchPlaceholder', 'جستجو در پروژه‌ها...')}
              className="ps-9"
            />
          </div>
          <Button variant="outline" size="icon">
            <Filter className="w-4 h-4" />
          </Button>
          {canCreateProject && (
            <Button onClick={() => setCreateOpen(true)} className="gap-1.5">
              <Plus className="w-4 h-4" />
              ایجاد پروژه
            </Button>
          )}
        </div>

        {/* Grid */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <ProjectCardSkeleton key={i} />
            ))}
          </div>
        ) : isError ? (
          <EmptyState
            icon={AlertTriangle}
            title={t('projects.loadError', 'بارگذاری پروژه‌ها ناموفق بود')}
            description={
              error instanceof Error
                ? error.message
                : t('projects.loadErrorDesc', 'ارتباط با سرور برقرار نشد. دوباره تلاش کنید.')
            }
            action={
              <Button variant="outline" onClick={() => refetch()}>
                {t('common.retry', 'تلاش مجدد')}
              </Button>
            }
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Search}
            title={
              search
                ? t('projects.noResults', 'پروژه‌ای با این مشخصات یافت نشد')
                : t('projects.noProjects', 'هنوز عضو هیچ پروژه‌ای نیستید')
            }
            description={
              search
                ? undefined
                : t('projects.noProjectsDesc', 'برای دسترسی به پروژه‌ها از مدیر سیستم درخواست عضویت کنید')
            }
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((project, i) => (
              <ProjectCard
                key={project.id}
                project={project}
                index={i}
                isFirst={i === 0 && !search}
                onClick={() => handleSelect(project)}
              />
            ))}

            {/* Super user platform panel card */}
            {user?.isSuperUser && (
              <button
                className="border border-dashed border-border rounded-xl p-5 text-start w-full transition-all hover:border-primary/50 hover:bg-muted/20 group"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(39,70,144,0.07) 0%, rgba(40,111,108,0.07) 100%)',
                }}
                onClick={() => navigate('/admin')}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Plus className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-primary">
                    {t('projects.platformPanel', 'پنل پلتفرم')}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('projects.platformPanelDesc', 'مدیریت کلی سیستم و پروژه‌ها')}
                </p>
              </button>
            )}
          </div>
        )}
      </div>

      <CreateProjectWizard
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setActiveProjectId(id)
          navigate(`/projects/${id}/overview`)
        }}
      />
    </div>
  )
}
